import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Avatar,
  AvatarGroup,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider,
  Badge
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  EmojiEvents as TrophyIcon,
  SportsEsports as GameIcon,
  TrendingUp as RemontadaIcon,
  LocalFireDepartment as PalizaIcon,
  Extension as ComboIcon,
  Shield as ResilienciaIcon,
  Person as SobrevivienteIcon,
  Bolt as RapidaIcon,
  Close as CloseIcon,
  Pets as BeastIcon,
  Science as UndeadIcon,
  Castle as HumanIcon,
  Park as NightElfIcon,
  Whatshot as OrcIcon,
  AcUnit as UndeadScourgeIcon,
  Diversity3 as RandomIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Block as BlockIcon,
  WatchLater as WatchLaterIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { es } from 'date-fns/locale';
import { format } from 'date-fns';
import api from '../services/api';
import RaceChip from './RaceChip';

// Función para obtener el color del tier
const getTierColor = (tier) => {
  switch (tier) {
    case 'Amistoso':
      return { bg: '#4caf50', text: '#fff' };
    case 'Amistoso c/ Arbitro':
      return { bg: '#2196f3', text: '#fff' };
    case 'Arena':
      return { bg: '#ff9800', text: '#fff' };
    case 'Torneo':
      return { bg: '#f44336', text: '#fff' };
    default:
      return { bg: 'action.hover', text: 'text.primary' };
  }
};

// Función para obtener el icono del highlight
const getHighlightIcon = (highlight) => {
  switch (highlight) {
    case 'Remontada':
      return <RemontadaIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Paliza':
      return <PalizaIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Combo':
      return <ComboIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Resiliencia':
      return <ResilienciaIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Sobreviviente':
      return <SobrevivienteIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Rapida':
      return <RapidaIcon sx={{ fontSize: '1.2rem' }} />;
    default:
      return null;
  }
};

// Función para obtener el estado de aprobación del jugador
const getPlayerApprovalStatus = (player, teamNumber) => {
  try {
    if (!player) return false;
    return player.approval_status;
  } catch (error) {
    console.warn('Error checking player approval status:', error);
    return false;
  }
};

// Componente principal MatchCard
const MatchCard = ({ match, usersMap, onClick }) => {
  const navigate = useNavigate();
  const [decksMap, setDecksMap] = useState({});

  useEffect(() => {
    const fetchDecks = async () => {
      try {
        // Obtener todos los IDs de mazos únicos
        const deckIds = new Set();
        if (match.team1?.decks) {
          match.team1.decks.forEach(deck => deckIds.add(deck.deck_id));
        }
        if (match.team2?.decks) {
          match.team2.decks.forEach(deck => deckIds.add(deck.deck_id));
        }

        if (deckIds.size === 0) return;

        // Obtener todos los mazos de una vez
        const response = await api.get('/api/decks', {
          timeout: 5000
        });
        
        // Crear un mapa de ID a información del mazo
        const decksMap = new Map();
        response.data.forEach(deck => {
          if (deckIds.has(deck.id)) {
            decksMap.set(deck.id, deck);
          }
        });

        // Marcar mazos no encontrados como eliminados
        deckIds.forEach(deckId => {
          if (!decksMap.has(deckId)) {
            decksMap.set(deckId, {
              id: deckId,
              name: `Mazo Eliminado (ID: ${deckId})`,
              raza: 'Desconocida'
            });
          }
        });

        setDecksMap(decksMap);
      } catch (error) {
        console.error('Error fetching decks:', error);
        // En caso de error, crear un mapa con mazos eliminados
        const deckIds = new Set();
        if (match.team1?.decks) {
          match.team1.decks.forEach(deck => deckIds.add(deck.deck_id));
        }
        if (match.team2?.decks) {
          match.team2.decks.forEach(deck => deckIds.add(deck.deck_id));
        }
        
        const errorDecksMap = new Map();
        deckIds.forEach(deckId => {
          errorDecksMap.set(deckId, {
            id: deckId,
            name: `Mazo Eliminado (ID: ${deckId})`,
            raza: 'Desconocida'
          });
        });
        
        setDecksMap(errorDecksMap);
      }
    };

    fetchDecks();
  }, [match]);

  const getPlayerDecks = (teamNumber) => {
    const team = teamNumber === 1 ? match.team1 : match.team2;
    if (!team?.decks) return [];
    
    const decks = team.decks.map(deck => {
      if (!deck) {
        return {
          name: 'Mazo no disponible',
          race: 'Desconocida'
        };
      }
      
      if (deck.deck_raza) {
        return {
          name: deck.deck_name || 'Mazo sin nombre',
          race: deck.deck_raza
        };
      }
      
      const deckInfo = decksMap[deck.deck_id];
      if (deckInfo) {
        return {
          name: deckInfo.name || 'Mazo sin nombre',
          race: deckInfo.raza
        };
      }
      
      return {
        name: `Mazo Eliminado (ID: ${deck.deck_id})`,
        race: 'Desconocida'
      };
    }).filter(Boolean);

    return decks;
  };

  // Función para obtener los nombres de usuario a partir de los jugadores
  const getUsernamesFromPlayers = (players) => {
    if (!Array.isArray(players)) return [];
    return players.map(player => ({
      id: player.user_id,
      name: usersMap[player.user_id] || player.user_id
    }));
  };

  const team1Players = getUsernamesFromPlayers(match.team1?.players || []);
  const team2Players = getUsernamesFromPlayers(match.team2?.players || []);
  const isTeam1Winner = match.winning_team === 'team1';

  const handleClick = (e) => {
    if (onClick) {
      onClick(e, match);
    } else {
      navigate(`/match/${match.id}`);
    }
  };

  return (
    <Paper
      elevation={0}
      onClick={handleClick}
      sx={{
        p: 1.8,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'rgba(25, 118, 210, 0.4)',
        bgcolor: 'background.paper',
        transition: 'all 0.2s',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          borderColor: 'primary.main',
        }
      }}
    >
      <Stack spacing={1.5}>
        {/* Match Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-start',
          gap: 0.8,
          flexWrap: 'wrap'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 0.5,
          }}>
            <ScheduleIcon sx={{ 
              color: 'text.secondary',
              fontSize: '1.26rem'
            }} />
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '0.84rem'
              }} 
              noWrap
            >
              {dayjs(match.match_date).format('DD/MM/YYYY HH:mm')}
            </Typography>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 0.8,
            ml: 'auto'
          }}>
            <Chip
              icon={<GameIcon sx={{ 
                color: 'text.secondary',
                fontSize: '1.26rem'
              }} />}
              label={match.match_mode}
              size="small"
              sx={{ 
                height: '23px',
                bgcolor: 'action.hover',
                color: 'text.primary',
                '& .MuiChip-label': { 
                  fontSize: '0.735rem',
                  px: 0.8
                }
              }}
            />
            <Chip
              icon={<TrophyIcon sx={{ 
                color: getTierColor(match.tier).text,
                fontSize: '1.2rem'
              }} />}
              label={match.tier}
              size="small"
              sx={{ 
                height: '22px',
                bgcolor: getTierColor(match.tier).bg,
                color: getTierColor(match.tier).text,
                '& .MuiChip-label': { 
                  fontSize: '0.7rem',
                  px: 0.8
                }
              }}
            />
          </Box>
        </Box>

        {/* Teams Section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          py: 0.8
        }}>
          {/* Team 1 */}
          <Stack alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 1,
              width: '100%',
              justifyContent: 'center'
            }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 'bold',
                  color: isTeam1Winner ? '#1976d2' : 'text.secondary',
                  fontSize: { xs: '1.8rem', sm: '2.2rem' },
                  minWidth: '60px',
                  textAlign: 'center'
                }}
              >
                {match.team1?.score || 0}
              </Typography>
              <Box sx={{ 
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <Stack spacing={0.5} alignItems="center">
                  <AvatarGroup 
                    max={2}
                    sx={{
                      '& .MuiAvatar-root': { 
                        width: 26.8,
                        height: 26.8,
                        fontSize: '0.76rem',
                        border: '2px solid',
                        borderColor: isTeam1Winner ? '#1976d2' : 'grey.500',
                        marginLeft: '-8px',
                        '&:first-of-type': {
                          marginLeft: 0
                        }
                      }
                    }}
                  >
                    {team1Players.map((player, idx) => (
                      <Badge
                        key={idx}
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: 'background.paper',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <ApprovedIcon
                              sx={{
                                fontSize: 10,
                                color: 'success.main',
                              }}
                            />
                          </Box>
                        }
                      >
                        <Avatar 
                          sx={{ 
                            bgcolor: isTeam1Winner ? '#1976d2' : 'grey.700',
                            color: 'white'
                          }}
                        >
                          {player.name[0]?.toUpperCase()}
                        </Avatar>
                      </Badge>
                    ))}
                  </AvatarGroup>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
                    <Typography 
                      variant="body2" 
                      fontWeight={isTeam1Winner ? 'bold' : 'normal'}
                      sx={{ 
                        fontSize: { xs: '0.735rem', sm: '0.86rem' },
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: isTeam1Winner ? '#1976d2' : 'text.secondary'
                      }}
                    >
                      {team1Players.map((player, idx) => (
                        <React.Fragment key={idx}>
                          {player.name}{idx < team1Players.length - 1 ? ', ' : ''}
                        </React.Fragment>
                      ))}
                    </Typography>
                    {team1Players.map((player, idx) => {
                      const playerDeck = getPlayerDecks(1)[idx];
                      return playerDeck?.race && (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <RaceChip race={playerDeck.race} size="small" />
                        </Box>
                      );
                    })}
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Stack>

          {/* VS */}
          <Typography 
            variant="body1" 
            sx={{ 
              mx: 1,
              fontSize: { xs: '0.86rem', sm: '0.99rem' },
              color: 'text.secondary',
              fontWeight: 'bold'
            }}
          >
            VS
          </Typography>

          {/* Team 2 */}
          <Stack alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 1,
              width: '100%',
              flexDirection: 'row-reverse',
              justifyContent: 'center'
            }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 'bold',
                  color: !isTeam1Winner ? '#1976d2' : 'text.secondary',
                  fontSize: { xs: '1.8rem', sm: '2.2rem' },
                  minWidth: '60px',
                  textAlign: 'center'
                }}
              >
                {match.team2?.score || 0}
              </Typography>
              <Box sx={{ 
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <Stack spacing={0.5} alignItems="center">
                  <AvatarGroup 
                    max={2}
                    sx={{
                      '& .MuiAvatar-root': { 
                        width: 26.8,
                        height: 26.8,
                        fontSize: '0.76rem',
                        border: '2px solid',
                        borderColor: !isTeam1Winner ? '#1976d2' : 'grey.500',
                        marginRight: '-8px',
                        '&:last-of-type': {
                          marginRight: 0
                        }
                      }
                    }}
                  >
                    {team2Players.map((player, idx) => (
                      <Badge
                        key={idx}
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: 'background.paper',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <ApprovedIcon
                              sx={{
                                fontSize: 10,
                                color: 'success.main',
                              }}
                            />
                          </Box>
                        }
                      >
                        <Avatar 
                          sx={{ 
                            bgcolor: !isTeam1Winner ? '#1976d2' : 'grey.700',
                            color: 'white'
                          }}
                        >
                          {player.name[0]?.toUpperCase()}
                        </Avatar>
                      </Badge>
                    ))}
                  </AvatarGroup>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
                    <Typography 
                      variant="body2" 
                      fontWeight={!isTeam1Winner ? 'bold' : 'normal'}
                      sx={{ 
                        fontSize: { xs: '0.735rem', sm: '0.86rem' },
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: !isTeam1Winner ? '#1976d2' : 'text.secondary'
                      }}
                    >
                      {team2Players.map((player, idx) => (
                        <React.Fragment key={idx}>
                          {player.name}{idx < team2Players.length - 1 ? ', ' : ''}
                        </React.Fragment>
                      ))}
                    </Typography>
                    {team2Players.map((player, idx) => {
                      const playerDeck = getPlayerDecks(2)[idx];
                      return playerDeck?.race && (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <RaceChip race={playerDeck.race} size="small" />
                        </Box>
                      );
                    })}
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Stack>
        </Box>

        {/* Match Info - Highlights */}
        {(() => {
          try {
            if (!match.highlights) return null;
            const highlights = Array.isArray(match.highlights) ? 
              match.highlights : 
              JSON.parse(match.highlights);

            if (Array.isArray(highlights) && highlights.length > 0) {
              return (
                <Stack 
                  direction="row" 
                  spacing={0.4}
                  justifyContent="center"
                  flexWrap="wrap"
                  sx={{ gap: 0.4 }}
                >
                  {highlights.map((highlight, index) => (
                    <Chip
                      key={index}
                      icon={getHighlightIcon(highlight.type)}
                      label={highlight.type}
                      size="small"
                      sx={{ 
                        height: '22px',
                        bgcolor: '#0277bd',
                        color: 'white',
                        '& .MuiChip-label': { 
                          fontSize: '0.7rem',
                          px: 0.8
                        },
                        '& .MuiChip-icon': {
                          color: 'white',
                          fontSize: '1.2rem'
                        }
                      }}
                    />
                  ))}
                </Stack>
              );
            }
            return null;
          } catch (error) {
            console.warn('Error parsing highlights:', error);
            return null;
          }
        })()}
      </Stack>
    </Paper>
  );
};

export default MatchCard; 